import * as angular from 'angular';
import { Growl } from 'angular/custom/services/growl.service';
import { migratedBiddingProcess } from 'angular/utils/migrated-auction-bidding-process';
import { DATES } from 'app/_constants/dates.constant';
import { USER_ROLES } from 'app/_constants/user-roles.constant';
import { bind } from 'app/_decorators/bind.decorator';
import { AuctionResource } from 'app/_resources/auction.resource';
import { ActivitiesService } from 'app/_services/activities.service';
import { AuctionsOverviewService } from 'app/_services/auctions-overview.service';
import { UtilService } from 'app/_services/util.service';
import {
    AuctionTileRequestParams,
    PageSorting,
    PagingSetting
} from '../custom';

type ColumnFilterOptions = {
    periodType: string | null;
    slot: number | null;
    phase: string | null;
    // resetable filters start here:
    networkPointName: string | null;
    tso: string | null;
    direction: string | null;
    capacityCategory: string | null;
    runtimeFrom: string | null;
    runtimeTo: string | null;
    'marketable-min': number | null;
    'marketable-max': number | null;
    'startingPrice-min': number | null;
    'startingPrice-max': number | null;
    // end of resetable filters
    networkPoint: string | null; // still needed? difference to networkPointName?
    favoritesOnly: boolean | null;
    ownTsoOnly: boolean | null;
};

/**
 * @ngdoc controller
 * @name AuctionListController
 * @memberof app
 *
 * @description
 * List Auctions for long and short term auctions.
 * A route change won't trigger a page reload. See "reloadOnSearch" in router config.
 *
 * @author flzemke.btc
 */
class AuctionListComponentCtrl implements ng.IController {
    readonly ACTION_TYPE: any;
    readonly INPUT_ADDON: string;

    colFilt: boolean = false;
    colFiltResetPossible: boolean = false;
    columnFilterOptions: ColumnFilterOptions = {
        periodType: null,
        slot: null,
        phase: null,
        networkPointName: null,
        tso: null,
        direction: null,
        capacityCategory: null,
        runtimeFrom: null,
        runtimeTo: null,
        'marketable-min': null,
        'marketable-max': null,
        'startingPrice-min': null,
        'startingPrice-max': null,
        networkPoint: null,
        favoritesOnly: null,
        ownTsoOnly: null
    };

    pageSortingOptions: PageSorting = <PageSorting>{
        sortAscending: false
    };
    pagingSetting: PagingSetting = <PagingSetting>{};
    pageNumber: number = 1;

    loggedInUser: LoginResult;
    tsoReferenceId: number = null;

    uiLoading: boolean = true;
    uiLoadingAuctions: boolean;
    uiRequestError: boolean;
    uiDeleteBidError: boolean;
    uiCancellationError: boolean;
    uiShowBiddingForm: boolean[];
    uiShowAuctionDetails: boolean[];

    bidsInProgress: any[];
    auctions: any;
    auctionTile: any;

    isLongTermAuction: boolean;
    normalizedPeriodType: string;
    runtime: any = {};
    undiscounted: boolean = false;

    exportParams: any;

    runtimeFrom?: string; // duplicate to columnFilterOptions.runtimeFrom?
    runtimeTo?: string; // duplicate to columnFilterOptions.runtimeTo?
    auctionId: number | null = null;
    identifier: string;

    migratedBiddingProcess = migratedBiddingProcess;

    constructor(
        private $q: ng.IQService,
        private $location: ng.ILocationService,
        private Activity,
        private auctionResource: AuctionResource,
        private auctionsOverviewService: AuctionsOverviewService,
        private networkPointSearchService,
        private activitiesService: ActivitiesService,
        private log: ng.ILogService,
        private DATES: DATES,
        private platformSettingsService,
        private COLUMN_FILTER,
        private USER_ROLES: USER_ROLES,
        private growl: Growl,
        private utilService: UtilService
    ) {
        this.ACTION_TYPE = this.auctionsOverviewService.ACTION_TYPE;
        this.INPUT_ADDON =
            this.networkPointSearchService.INPUT_ADDON.QUICK_FILTER;
    }

    $onInit() {
        if (migratedBiddingProcess.isActive) return;
        if (
            this.loggedInUser &&
            this.loggedInUser.role === this.USER_ROLES.TSO_USER
        ) {
            this.tsoReferenceId = this.loggedInUser.referenceId;
        }

        this.fetchOptionsFromSearchParams();

        this.exportParams = {
            periodType: this.columnFilterOptions.periodType,
            slot: this.columnFilterOptions.slot,
            phase: this.columnFilterOptions.phase,
            runtimeFrom: this.runtimeFrom,
            runtimeTo: this.runtimeTo
        };

        if (this.identifier) {
            this.exportParams.calendarIdentifier = this.identifier;
        }

        this.normalizedPeriodType =
            this.auctionsOverviewService.getPeriodTypeByValue(
                this.columnFilterOptions.periodType
            ).NORMALIZED;

        this.normalizedPeriodType =
            this.auctionsOverviewService.getPeriodTypeByValue(
                this.columnFilterOptions.periodType
            ).NORMALIZED;

        this.isLongTermAuction =
            this.columnFilterOptions.periodType !== 'WITHINDAY' &&
            this.columnFilterOptions.periodType !== 'DAY';
        if (this.isLongTermAuction) {
            this.auctionId = null;
        }
    }

    afterPagingSettingInit() {
        const tilePromise = this.getTiles();
        let promises = [
            tilePromise,
            this.getActivities(tilePromise),
            this.getAuctions()
        ];

        this.$q.all(promises).finally(() => {
            this.uiLoading = false;
            if (this.auctionsOverviewService.bidDeletedSuccessfully) {
                this.showDeletedBidSuccessMessage();
                this.auctionsOverviewService.bidDeletedSuccessfully = false;
            }
        });
    }

    public switchToMigratedBiddingProcess() {
        migratedBiddingProcess.activate({
            slot: this.columnFilterOptions.slot,
            phase: this.columnFilterOptions.phase,
            periodType: this.columnFilterOptions.periodType,
            runtimeFrom: this.runtimeFrom,
            runtimeTo: this.runtimeTo,
            identifier: this.identifier,
            auctionId: this.auctionId
        });
    }

    public resetColumnFilter() {
        this.columnFilterOptions.networkPointName = null;
        this.columnFilterOptions.tso = null;
        this.columnFilterOptions.direction = null;
        this.columnFilterOptions.capacityCategory = null;
        this.columnFilterOptions.runtimeFrom = null;
        this.columnFilterOptions.runtimeTo = null;
        this.columnFilterOptions['marketable-min'] = null;
        this.columnFilterOptions['marketable-max'] = null;
        this.columnFilterOptions['startingPrice-min'] = null;
        this.columnFilterOptions['startingPrice-max'] = null;
    }

    public deleteFilterAllowed(filterName: keyof ColumnFilterOptions): boolean {
        return (
            filterName != 'periodType' &&
            filterName != 'slot' &&
            filterName != 'phase' &&
            filterName != 'favoritesOnly'
        );
    }

    public isArray(obj) {
        return angular.isArray(obj);
    }

    public deleteFilter(filterName: keyof ColumnFilterOptions) {
        // NOTE: sadly because "strict: true" isn't enabled we can't assign null directly,
        // even though it is technically correct
        (this.columnFilterOptions[filterName] as any) = null;
        this.getAuctionsWithoutAuctionIdFilter();
    }

    private fetchOptionsFromSearchParams() {
        const search: {
            [key: string]: string | undefined;
        } = this.$location.search();

        this.auctionId = search.auctionId ? +search.auctionId : null;

        this.columnFilterOptions.networkPointName =
            search.networkPointName || null;
        this.columnFilterOptions.tso = search.tso || null;
        this.columnFilterOptions.direction = search.direction || null;
        this.columnFilterOptions.capacityCategory =
            search.capacityCategory || null;
        this.columnFilterOptions.runtimeFrom = search.runtimeFrom || null;
        this.columnFilterOptions.runtimeTo = search.runtimeTo || null;
        this.columnFilterOptions['marketable-min'] = search['marketable-min']
            ? +search['marketable-min']
            : null;
        this.columnFilterOptions['marketable-max'] = search['marketable-max']
            ? +search['marketable-max']
            : null;
        this.columnFilterOptions['startingPrice-min'] = search[
            'startingPrice-min'
        ]
            ? +search['startingPrice-min']
            : null;
        this.columnFilterOptions['startingPrice-max'] = search[
            'startingPrice-max'
        ]
            ? +search['startingPrice-max']
            : null;

        if (
            this.columnFilterOptions.networkPointName !== null ||
            this.columnFilterOptions.tso !== null ||
            this.columnFilterOptions.direction !== null ||
            this.columnFilterOptions.capacityCategory !== null ||
            this.columnFilterOptions.runtimeFrom !== null ||
            this.columnFilterOptions.runtimeTo !== null ||
            this.columnFilterOptions['marketable-min'] !== null ||
            this.columnFilterOptions['marketable-max'] !== null ||
            this.columnFilterOptions['startingPrice-min'] !== null ||
            this.columnFilterOptions['startingPrice-max'] !== null
        ) {
            this.colFiltResetPossible = true;
        }

        this.columnFilterOptions.networkPoint = search.networkPoint || null;
        if (
            this.loggedInUser &&
            !this.auctionId &&
            (this.loggedInUser.role === 'SHIPPER_ADMIN' ||
                this.loggedInUser.role === 'SHIPPER_USER')
        ) {
            this.columnFilterOptions.favoritesOnly =
                search.favoritesOnly !== 'false';
        } else if (
            this.loggedInUser &&
            !this.auctionId &&
            this.loggedInUser.role === 'TSO_USER'
        ) {
            this.columnFilterOptions.ownTsoOnly = search.ownTsoOnly !== 'false';
        }
        this.columnFilterOptions.slot = search.slot ? +search.slot : 1;
        this.columnFilterOptions.phase = search.phase || 'OPEN';
        this.columnFilterOptions.periodType = search.periodType || 'WITHINDAY';
        this.runtimeFrom = search['runtime-from'];
        this.runtimeTo = search['runtime-to'];
        this.identifier = search.identifier;

        this.pageNumber = search.pageNumber ? +search.pageNumber : 1;
        if (search.sortAscending === 'true') {
            this.pageSortingOptions.sortAscending = true;
        }
        this.pageSortingOptions.sortColumn = search.sortColumn;
        this.pagingSetting.amountSetting = search.amountSetting as AmountType; // seems to default to 'KILO'
        this.pagingSetting.costSetting = search.costSetting as CostType; // seems to default to 'SUBUNIT'
        this.pagingSetting.pageSize = +(search.pageSize as string); // seems to default to '10'
    }

    private saveOptionsToSearchParams() {
        let locationParams = angular.extend(
            angular.copy(this.columnFilterOptions),
            angular.copy(this.pageSortingOptions),
            angular.copy(this.pagingSetting),
            {
                pageNumber: this.pageNumber,
                'runtime-from': this.runtimeFrom,
                'runtime-to': this.runtimeTo,
                auctionId: this.auctionId,
                identifier: this.identifier
            }
        );
        this.$location.search(
            this.utilService.objectLiteralToQueryParamString(locationParams)
        );
    }

    private showDeletedBidSuccessMessage() {
        this.growl.show('The bid has been deleted successfully.', 'success');
    }

    private showPlacedBidSuccessMessage() {
        this.growl.show('Your bid has been placed successfully.', 'success');
    }

    isCancelable(auction) {
        return (
            (angular.isDefined(this.loggedInUser) &&
                this.loggedInUser.role === this.USER_ROLES.TSO_USER &&
                !this.tsoReferenceId) ||
            (this.tsoReferenceId &&
                auction.networkPoint.tso0Id == this.tsoReferenceId &&
                !auction.networkPoint.bundled &&
                (auction.phase == 'PREOPEN' ||
                    auction.phase == 'PUBLISHED' ||
                    auction.phase == 'OPEN'))
        );
    }

    cancelAuction(index, auctionId) {
        this.auctionResource.cancelAuction(
            { auctionId: auctionId },
            () => {
                this.auctions.data.splice(index, 1);
            },
            (err: ng.IHttpPromiseCallbackArg<RestError>) => {
                const errorMessage =
                    err && err.data && err.data.message
                        ? err.data.message
                        : 'An unknown error occurred. Please, check back later.';
                this.uiCancellationError = true;
                this.growl.show(errorMessage, 'danger');
            }
        );
    }

    directBidLink(auctionId) {
        return (
            this.$location.search().auctionId &&
            this.$location.search().auctionId == auctionId
        );
    }

    getTiles() {
        let tileRequestParams: AuctionTileRequestParams = {};

        if (this.columnFilterOptions.phase) {
            // NOTE: According to AuctionTileRequestParams "phase" needs to be an
            // array, but that was never the case. Is it wrong?
            tileRequestParams.phase = this.columnFilterOptions.phase as any;
        }
        if (this.columnFilterOptions.slot) {
            tileRequestParams.slot = this.columnFilterOptions.slot;
        }
        if (this.columnFilterOptions.periodType) {
            // NOTE: columnFilterOptions.periodType is a string which was never validated,
            // but the old code assumed that it is a PeriodType
            tileRequestParams.periodType = this.columnFilterOptions
                .periodType as PeriodType;
        }
        if (this.identifier) {
            tileRequestParams.identifier = this.identifier;
        }

        if (!this.isLongTermAuction && this.runtimeFrom && this.runtimeTo) {
            angular.extend(tileRequestParams, {
                runtimeFrom: this.runtimeFrom,
                runtimeTo: this.runtimeTo
            });
        }

        return this.auctionResource.tile(
            tileRequestParams,
            (res) => {
                this.auctionTile = res;
                if (res.runtimeList) {
                    this.runtime =
                        this.auctionsOverviewService.getTotalRuntimeFromManyRuntimes(
                            res.runtimeList
                        );
                } else {
                    this.log.error(
                        '204 Response for Auctions.tile request with query params: ' +
                            JSON.stringify(tileRequestParams)
                    );
                }
            },
            () => {
                this.uiRequestError = true;
            }
        ).$promise;
    }

    getActivities(tilePromise?: angular.IPromise<any>) {
        if (!this.loggedInUser) {
            return this.$q.resolve();
        }

        // if tilePromise is provided we wait for it, before we continue to ensure tile is ready
        tilePromise = tilePromise || this.$q.resolve();

        return tilePromise.then(() =>
            this.auctionResource
                .calendars()
                .$promise.catch(
                    (err) => ({}) as { [key: string]: AuctionCalendarDto[] }
                )
                .then((res) => {
                    // find the correct publish date and use this one to request activities
                    // (we don't want to request too few days or we loose activities - see ISR-768,
                    // but we also don't always want to ask for the maximum (which is 90 days), because
                    // the request is *very* slow).
                    const calendarList =
                        res[this.columnFilterOptions.periodType] || [];
                    const calendar = calendarList.find(
                        (calendar) =>
                            (calendar.calendarIdentifier || '') ===
                                (this.identifier || '') &&
                            calendar.slot === this.columnFilterOptions.slot &&
                            this.auctionTile && // in case auctionTile would not be available yet, we stop here
                            this.auctionTile.runtimeList && // auctionTile was 204 we have no runtimeList
                            // it should be fine to just check the first item in `runtimeList`
                            // as they all should have the same publish date
                            calendar.runtime.start ===
                                this.auctionTile.runtimeList[0].start &&
                            calendar.runtime.end ===
                                this.auctionTile.runtimeList[0].end
                    );

                    let activityParams = {
                        category:
                            this.auctionsOverviewService.getAuctionCategoriesToRequest(
                                this.isLongTermAuction,
                                this.columnFilterOptions.phase
                            ),
                        phase: this.columnFilterOptions.phase,
                        periodType: this.columnFilterOptions.periodType,
                        cached: false,
                        from: calendar ? calendar.publishDate : undefined // if no calendar can be found we use the server default (30 days)
                    };

                    return this.Activity.query(
                        activityParams,
                        (res) => {
                            this.bidsInProgress = this.activitiesService.map(
                                res.data
                            ).activities;
                        },
                        () => {
                            this.uiRequestError = true;
                        }
                    ).$promise;
                })
        );
    }

    getAuctionsWithoutAuctionIdFilter() {
        this.auctionId = null;
        this.getAuctions();
    }

    getAuctions() {
        let param: any = {
            start: (this.pageNumber - 1) * this.pagingSetting.pageSize,
            pageSize: this.pagingSetting.pageSize,
            amountSetting: this.pagingSetting.amountSetting,
            costSetting: this.pagingSetting.costSetting,
            sortAscending: this.pageSortingOptions.sortAscending,
            sortColumn: this.pageSortingOptions.sortColumn,
            withoutBids: true
        };

        angular.extend(param, this.columnFilterOptions);

        if (this.auctionId) {
            angular.extend(param, { auctionId: [this.auctionId] });
        }

        if (this.identifier) {
            angular.extend(param, { calendarIdentifier: [this.identifier] });
        }

        if (this.runtimeFrom && this.runtimeTo) {
            angular.extend(param, {
                runtimeFrom: this.runtimeFrom,
                runtimeTo: this.runtimeTo
            });
        }

        if (!this.isLongTermAuction) {
            angular.extend(param, {
                runtimeExactMatch: true
            });
        }

        this.uiLoadingAuctions = true;
        return this.auctionResource
            .query(
                param,
                (res) => {
                    this.auctions = res;
                },
                () => {
                    this.uiRequestError = true;
                }
            )
            .$promise.finally(() => {
                this.uiLoadingAuctions = false;
                this.saveOptionsToSearchParams();
            });
    }

    changePageNumber(newPageNumber) {
        this.pageNumber = newPageNumber;
        this.uiShowBiddingForm = [];
        this.uiShowAuctionDetails = [];
        this.getAuctions();
    }

    changeOrder(sortColumn) {
        this.pageSortingOptions.sortColumn = sortColumn;
        this.pageSortingOptions.sortAscending =
            !this.pageSortingOptions.sortAscending;
        this.uiShowBiddingForm = [];
        this.uiShowAuctionDetails = [];
        this.getAuctions();
    }

    @bind
    submitSelectedNetworkPoints(selectedNetworkPoints) {
        this.columnFilterOptions.networkPoint =
            this.platformSettingsService.extractIdsOfNetworkPoints(
                selectedNetworkPoints
            );
        this.getAuctionsWithoutAuctionIdFilter();
    }

    deleteBid(index, bidId) {
        this.auctionResource.deleteBid(
            { id: bidId },
            () => {
                delete this.bidsInProgress[index];
                this.getAuctions();
                this.getActivities();
                this.showDeletedBidSuccessMessage();
            },
            () => {
                this.uiDeleteBidError = true;
            }
        );
    }

    closeFoldOut(index) {
        this.uiShowBiddingForm[index] = false;
        this.uiShowAuctionDetails[index] = false;
    }

    onBidSuccess() {
        for (let i in this.uiShowBiddingForm) {
            if (!this.uiShowBiddingForm.hasOwnProperty(i)) {
                continue;
            }
            this.closeFoldOut(i);
        }

        this.showPlacedBidSuccessMessage();
        this.getActivities();
        this.getAuctions();
    }

    /**
     * Reacts upon changes of undiscounted checkbox in the pkpStaBid directive.
     *
     * @param {Object[]} undiscounted
     * @param {Number} index
     * @returns {void}
     */
    onUndiscountedChange(undiscounted, index) {
        this.auctions.data[index].undiscounted = undiscounted;
    }

    // calculateMarketableValueWithMixedUnits(auction) {
    //     let factor = auction.original.unit != auction.marketable.unit ? 24 : 1;
    //     return Math.floor(auction.marketable.value.value / factor);
    // }
    //
    // calculateMarketableUnitWithMixedUnits(auction) {
    //     return auction.original.unit != auction.marketable.unit
    //         ? auction.original.unit
    //         : auction.marketable.unit;
    // }
}

angular.module('app').component('pkpAuctionList', {
    controller: AuctionListComponentCtrl,
    template: require('app/auctions-list/auction-list.component.html'),
    bindings: {
        loggedInUser: '<'
    }
});
